/**
 * GlobalMagicSign — global (cross-company) Magic Sign content rendered inside the
 * Magic Upload drawer's Magic Sign tab when there is no company context.
 *
 * Pattern reference:
 * - Table: src/common/MagicSign/components/MagicSignTable/MagicSignTable.tsx (reused with showOrgColumn=true)
 * - Logic hook: src/common/MagicSign/useMagicSignLogic.tsx (mirrored pattern)
 */
import React, { useCallback, useMemo, useState } from 'react';
import styles from './GlobalMagicSign.module.scss';
import useGlobalMagicSignLogic from './useGlobalMagicSignLogic';
import MagicSignTable from '../../common/MagicSign/components/MagicSignTable/MagicSignTable';
import AuditLogDrawer from '../../common/MagicSign/components/AuditLogDrawer/AuditLogDrawer';
import CancelConfirmDialog from '../../common/MagicSign/components/CancelConfirmDialog/CancelConfirmDialog';
import MagicSignFilterPills, { FilterPill } from '../../common/MagicSign/components/MagicSignFilterPills/MagicSignFilterPills';
import MagicSignPreviewer from '../../common/MagicSign/MagicSignPreviewer';
import MagicUploadCenter from '../../common/MagicUpload/components/MagicUploadCenter/MagicUploadCenter';
import ComposeEmailModal from '../../common/ComposeEmailModal/ComposeEmailModal';
import { sendMagicSignEmail } from '../../common/MagicSign/MagicSignService';
import { GlobalMagicSignRow, MagicSignStatus, SignStatus } from '../../common/MagicSign/types';
import { buildMagicSignNoteWithLinks } from '../../common/MagicSign/MagicSign';
import {
    MAGIC_SIGN_ALLOWED_TYPES,
    MAGIC_SIGN_PDF_ONLY_TOAST,
    MAGIC_SIGN_STATUS_CONFIG,
    SIGN_TYPE_OPTIONS,
} from '../../common/MagicSign/constants';
import { useAppSelector } from '../../app/hooks';
import { toast } from 'react-toastify';

interface GlobalMagicSignProps {
    /** Gates the initial fetch — set false while the drawer tab is hidden */
    isActive?: boolean;
}

const GlobalMagicSign: React.FC<GlobalMagicSignProps> = ({ isActive = true }) => {
    const logic = useGlobalMagicSignLogic({ isActive });
    const platformData = useAppSelector((state) => state.user.domainMetadata);
    const actorName: string = platformData?.displayName ?? '';

    const {
        rows,
        loading,
        configMeta,
        polusDocTypes,
        activeRowId,
        cancelConfirmId,
        setCancelConfirmId,
        auditLogOpen,
        setAuditLogOpen,
        auditLogEvents,
        auditLogLoading,
        auditLogRowName,
        docIdToView,
        setDocIdToView,
        docNameToView,
        activeFilters,
        handleFiltersChange,
        handleGlobalFileUpload,
        handleUpdateDocType,
        handleUpdateSignType,
        handleUpdateSignatories,
        handleCancelConfirm,
        handleViewAuditLog,
        handleDownloadDocument,
        handleCopySigningLinks,
        handleRowClick,
        handleSendReminder,
        handleGenerateLinks,
        handleAssociateCompany,
        userName,
        currentUserId,
        removeFilterValue,
        clearAllFilters,
        refreshList,
    } = logic;
    const pnoId: string = platformData?.investorOrgId ?? '';

    // Build the active-filter pills (Magic Upload parity) — one removable pill per active
    // filter value, with labels resolved from configMeta + loaded rows. The auto-applied
    // "my requests" filter shows as the current user's name, exactly like Magic Upload's
    // uploader chip.
    const filterPills: FilterPill[] = useMemo(() => {
        const requesterNameById = new Map<string, string>();
        const orgNameById = new Map<string, string>();
        const sigNameByEmail = new Map<string, string>();
        rows.forEach((r) => {
            if (r.requestedByUserId) requesterNameById.set(r.requestedByUserId, r.requestedByName ?? r.requestedByUserId);
            const gr = r as GlobalMagicSignRow;
            if (gr.orgId) orgNameById.set(gr.orgId, gr.orgName ?? gr.orgId);
            (r.signatories ?? []).forEach((s) => {
                if (s.email) sigNameByEmail.set(s.email, s.name ?? s.email);
            });
        });

        const docTypeLabel = (v: string) => configMeta?.documentTypes?.find((dt) => dt.id === v)?.label ?? v;
        const signTypeLabel = (v: string) => SIGN_TYPE_OPTIONS.find((o) => o.id === v)?.label ?? v;
        const statusLabel = (v: string) => MAGIC_SIGN_STATUS_CONFIG[v as MagicSignStatus]?.label ?? v;
        const requesterLabel = (v: string) => (v === currentUserId ? userName : requesterNameById.get(v) ?? v);

        const af = activeFilters ?? {};
        const pills: FilterPill[] = [];
        (af.requestedBy ?? []).forEach((v) => pills.push({ key: 'requestedBy', value: v, label: requesterLabel(v) }));
        (af.orgIds ?? []).forEach((v) => pills.push({ key: 'orgIds', value: v, label: orgNameById.get(v) ?? v }));
        (af.documentTypes ?? []).forEach((v) => pills.push({ key: 'documentTypes', value: v, label: docTypeLabel(v) }));
        (af.signatoryEmails ?? []).forEach((v) => pills.push({ key: 'signatoryEmails', value: v, label: sigNameByEmail.get(v) ?? v }));
        (af.signTypes ?? []).forEach((v) => pills.push({ key: 'signTypes', value: v, label: signTypeLabel(v) }));
        (af.statuses ?? []).forEach((v) => pills.push({ key: 'statuses', value: v, label: statusLabel(v) }));
        return pills;
    }, [rows, configMeta, activeFilters, userName, currentUserId]);

    // PDF-only filter for the global drop zone — mirrors MagicSign.tsx's handleFilesSelected.
    // handleGlobalFileUpload takes File[] directly — no FileList-shaped object needs to be faked.
    const handleGlobalFilesSelected = useCallback(
        (files: FileList) => {
            const filtered: File[] = [];
            for (let i = 0; i < files.length; i++) {
                if (!MAGIC_SIGN_ALLOWED_TYPES.includes(files[i].type)) {
                    toast.error(MAGIC_SIGN_PDF_ONLY_TOAST);
                } else {
                    filtered.push(files[i]);
                }
            }
            if (filtered.length === 0) return;
            handleGlobalFileUpload(filtered);
        },
        [handleGlobalFileUpload],
    );

    const [emailModalRow, setEmailModalRow] = useState<GlobalMagicSignRow | null>(null);
    // 'reminder' → the compose modal was opened from Send Reminder; on send we fire the
    // reminder (which only notifies unsigned signatories) rather than the first signing email.
    const [emailModalMode, setEmailModalMode] = useState<'email' | 'reminder'>('email');

    // Generate links action — called from 3-dot menu "Generate Links"
    const handleGenerateLinksAction = useCallback(
        async (row: GlobalMagicSignRow) => {
            if (!row.signatories?.length) {
                toast.error('Select at least one signatory before generating links.');
                return;
            }
            await handleGenerateLinks(row.id);
        },
        [handleGenerateLinks],
    );

    // Route send-email/send-reminder based on new status model + emailSentAt.
    // GENERATED_LINKS && !emailSentAt → email mode
    // GENERATED_LINKS && emailSentAt  → reminder mode
    // PARTIAL_SIGNED                  → reminder mode
    const handleSendForSigning = useCallback(async (row: GlobalMagicSignRow) => {
        const isReminder =
            (row.status === MagicSignStatus.GENERATED_LINKS && !!row.emailSentAt) || row.status === MagicSignStatus.PARTIAL_SIGNED;
        if (isReminder) {
            setEmailModalMode('reminder');
            setEmailModalRow(row);
            return;
        }
        if (row.status === MagicSignStatus.GENERATED_LINKS && !row.emailSentAt) {
            if (!row.signatories?.length) {
                toast.error('Select at least one signatory before sending.');
                return;
            }
            setEmailModalMode('email');
            setEmailModalRow(row);
        }
    }, []);

    const handleGlobalEmailSend = useCallback(
        async (recipients: Array<{ emailId: string; userId?: string }>, noteHtml: string) => {
            if (!emailModalRow) return;
            if (emailModalMode === 'reminder') {
                // Reminder is sent server-side to the pending signatories only.
                await handleSendReminder(emailModalRow.id);
                setEmailModalRow(null);
                return;
            }
            const recipientEmails = recipients.map((r) => r.emailId).filter(Boolean);
            const note = noteHtml && noteHtml !== '<div></div>' ? noteHtml : undefined;
            try {
                await sendMagicSignEmail({ id: emailModalRow.id, recipientEmails, note });
                toast.success('Signing email sent successfully.');
                refreshList();
                setEmailModalRow(null);
            } catch (error) {
                console.error('[GlobalMagicSign] Failed to send signing email', error);
                toast.error(error instanceof Error && error.message ? error.message : 'Failed to send signing email. Please try again.');
            }
        },
        [emailModalRow, emailModalMode, handleSendReminder, refreshList],
    );

    // Memoized so ComposeEmailModal's fetchUsers effect (keyed on initialRecipients) only
    // refetches org users when the modal actually opens for a different row/mode — a fresh
    // .filter().map() literal here would change identity every render and refetch on every
    // parent re-render while the modal stays open.
    const emailModalRecipients = useMemo(
        () =>
            (emailModalRow?.signatories ?? [])
                .filter((s) => (emailModalMode === 'reminder' ? s.signStatus !== SignStatus.SIGNED : true))
                .map((s) => ({
                    emailId: s.email,
                    userId: s.userId,
                    name: s.name,
                })),
        [emailModalRow, emailModalMode],
    );

    // Audit drawer also needs the org name of the selected row
    const auditLogOrgName = auditLogRowName && rows.find((r) => r.fileName === auditLogRowName)?.orgName;

    return (
        <div className={styles.Container} data-testid="global-magic-sign-tab">
            {/* Global drop zone — orphan upload: no company context at upload time */}
            <MagicUploadCenter
                tabState="all"
                fileData={[]}
                setFileData={() => {}}
                orientation="vertical"
                onFilesSelected={handleGlobalFilesSelected}
                acceptTypes="application/pdf"
                dropText="Drag and drop any data"
            />
            {/* Active-filter pills + Clear All — matches Magic Upload's FilteredPills design and
                behaviour. Includes the auto-applied "my requests" filter as the current user's name. */}
            <MagicSignFilterPills pills={filterPills} onRemove={removeFilterValue} onClearAll={clearAllFilters} />
            <MagicSignTable
                rows={rows}
                loading={loading}
                configMeta={configMeta}
                polusDocTypes={polusDocTypes}
                activeRowId={activeRowId}
                highlightedRowId={null}
                showOrgColumn
                activeFilters={activeFilters}
                onFiltersChange={handleFiltersChange}
                onRowClick={handleRowClick}
                onUpdateDocType={handleUpdateDocType}
                onUpdateSignType={handleUpdateSignType}
                onSaveSignatories={handleUpdateSignatories}
                onGenerateLinks={(row) => handleGenerateLinksAction(row as GlobalMagicSignRow)}
                onSendEmail={(row) => handleSendForSigning(row as GlobalMagicSignRow)}
                onViewAuditLog={handleViewAuditLog}
                onDownloadDocument={handleDownloadDocument}
                onCopySigningLinks={handleCopySigningLinks}
                onCancelRequest={(id) => setCancelConfirmId(id)}
                pnoId={pnoId}
                onAssociateOrg={handleAssociateCompany}
            />

            <AuditLogDrawer
                open={auditLogOpen}
                onClose={() => setAuditLogOpen(false)}
                documentName={auditLogRowName}
                companyName={auditLogOrgName ?? undefined}
                events={auditLogEvents}
                loading={auditLogLoading}
            />

            {emailModalRow && (
                <ComposeEmailModal
                    isOpen={!!emailModalRow}
                    headerTitle={emailModalMode === 'reminder' ? 'Reminder' : 'Email'}
                    headerSubTitle={emailModalRow.fileName}
                    initialRecipients={emailModalRecipients}
                    initialNote={buildMagicSignNoteWithLinks({
                        documentType: emailModalRow.documentType,
                        actorName,
                        signingLinks: emailModalRow.signingLinks ?? [],
                    })}
                    onSendOverride={handleGlobalEmailSend}
                    onCloseOverride={() => setEmailModalRow(null)}
                />
            )}

            <MagicSignPreviewer docIdToView={docIdToView} setDocIdToView={setDocIdToView} docName={docNameToView} />

            {cancelConfirmId && (
                <CancelConfirmDialog onConfirm={() => handleCancelConfirm(cancelConfirmId)} onDismiss={() => setCancelConfirmId(null)} />
            )}
        </div>
    );
};

export default GlobalMagicSign;
